import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { cn } from "@utils/index"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cofarte-purple/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-cofarte-purple text-white hover:bg-cofarte-purple-dark shadow-sm",
        destructive: "bg-red-600 text-white hover:bg-red-700",
        outline: "border border-gray-300 bg-white hover:bg-gray-50 hover:border-cofarte-purple/50 text-gray-700",
        secondary: "bg-cofarte-green text-white hover:bg-cofarte-green-dark shadow-sm",
        ghost: "hover:bg-gray-100 text-gray-700 hover:text-cofarte-purple",
        link: "text-cofarte-purple underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const childContent = asChild 
      ? React.Children.only(children) as React.ReactElement
      : null
    
    if (asChild && childContent) {
      return React.cloneElement(childContent, {
        className: cn(buttonVariants({ variant, size, className }), childContent.props.className),
        ref,
        ...props,
      })
    }
    
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
